import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  href: string;
  done: boolean;
}

/**
 * Loads checklist completion state for the Getting Started widget.
 * All five tables are queried in parallel (Promise.all) and combined into
 * the same item list as the original loadProgress, so behavior is identical.
 * Errors are swallowed (returns empty array) to match the original silent-hide behaviour.
 */
export function useGettingStartedChecklist(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['getting-started-checklist', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<ChecklistItem[]> => {
      try {
        const [
          companyInfoResult,
          servicesResult,
          teamResult,
          jobsResult,
          hoursResult,
        ] = await Promise.all([
          supabase
            .from('company_info')
            .select('logo_url, phone_number')
            .eq('organization_id', orgId!)
            .maybeSingle(),
          supabase
            .from('services')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', orgId!)
            .eq('is_active', true),
          supabase
            .from('team_members')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', orgId!),
          supabase
            .from('jobs')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', orgId!),
          supabase
            .from('business_hours')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', orgId!)
            .eq('is_open', true),
        ]);

        return [
          {
            id: 'logo',
            title: 'Add your logo',
            description:
              'Upload your business logo so it shows on your website and emails.',
            href: '/admin/company-info',
            done: Boolean(companyInfoResult.data?.logo_url),
          },
          {
            id: 'services',
            title: 'Review your services',
            description:
              'We pre-filled a few services — edit prices and details to match your business.',
            href: '/admin/jobs-pricing',
            done: (servicesResult.count ?? 0) >= 1,
          },
          {
            id: 'hours',
            title: 'Set your operational hours',
            description: 'Tell customers when you take bookings.',
            href: '/admin/operational-hours',
            done: (hoursResult.count ?? 0) >= 1,
          },
          {
            id: 'team',
            title: 'Add a team member',
            description: 'Invite a cleaner or supervisor so jobs can be assigned.',
            href: '/admin/team',
            done: (teamResult.count ?? 0) >= 1,
          },
          {
            id: 'job',
            title: 'Schedule your first job',
            description: 'Create a test job to see how the calendar works.',
            href: '/admin/schedule',
            done: (jobsResult.count ?? 0) >= 1,
          },
        ];
      } catch {
        // silently hide the checklist on error rather than leaving it stuck loading
        return [];
      }
    },
  });
}
